const normalizeCatalogText = (value = "") => String(value)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/gu, "")
  .replace(/\s+/gu, " ")
  .trim()
  .toLocaleUpperCase("es-AR");

function catalogEntry({
  id,
  value,
  label = value,
  aliases = [],
  fueros = [],
  categories = [],
  circunscripcion = null,
  instance = null,
  status = "active",
}) {
  return Object.freeze({
    id,
    value,
    label,
    aliases: Object.freeze(aliases),
    fueros: Object.freeze(fueros),
    categories: Object.freeze(categories),
    circunscripcion,
    instance,
    status,
  });
}

export const MENDOZA_WORKFLOW_CATALOG_PROPERTIES = Object.freeze([
  "fuero",
  "procedure_type",
  "materia",
  "organismo",
]);

export const MENDOZA_WORKFLOW_FUEROS = Object.freeze([
  catalogEntry({ id: "civil", value: "CIVIL", label: "Civil" }),
  catalogEntry({ id: "concursal", value: "CONCURSAL", label: "Concursal" }),
  catalogEntry({
    id: "contravencional",
    value: "CONTRAVENCIONAL",
    label: "Contravencional",
  }),
  catalogEntry({ id: "familia", value: "FAMILIA", label: "Familia" }),
  catalogEntry({ id: "laboral", value: "LABORAL", label: "Laboral" }),
  catalogEntry({
    id: "paz",
    value: "JUZGADO DE PAZ",
    label: "Paz",
    aliases: ["PAZ"],
  }),
  catalogEntry({
    id: "penal_colegiado",
    value: "PENAL COLEGIADO",
    label: "Penal Colegiado",
  }),
  catalogEntry({
    id: "penal_juvenil",
    value: "PENAL JUVENIL",
    label: "Penal Juvenil",
  }),
  catalogEntry({ id: "tributario", value: "TRIBUTARIO", label: "Tributario" }),
  catalogEntry({
    id: "civil_appellate_raw",
    value: "CIVIL,MINAS,PAZ Y TRIBUTARIO",
    label: "Civil, Minas, Paz y Tributario · Cámara",
    fueros: ["civil"],
  }),
  catalogEntry({
    id: "supreme_court_raw",
    value: "SUPREMA CORTE",
    label: "Suprema Corte · instancia",
    fueros: ["supreme"],
  }),
]);

const CATEGORY_DEFINITIONS = [
  ["CAMARA CIVIL-SORTEO COMUN-ESPECIAL", ["civil"], "appeal", "historical"],
  ["CAMARA CIVIL-SORTEO COMUN-ORDINARIO", ["civil"], "appeal", "historical"],
  ["CIVIL", ["civil", "supreme"]],
  ["CIVIL - SORTEO COMUN - DE CONOCIMIENTO", ["civil"]],
  ["CIVIL - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL", ["civil"]],
  ["CIVIL - SORTEO COMUN - ESPECIAL Y AUTÓNOMO", ["civil"]],
  ["CIVIL - SORTEO COMUN - GARANTÍAS CONSTITUCIONALES", ["civil"]],
  ["CIVIL - SORTEO COMUN - PIEZA SEPARADA", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - DE CONOCIMIENTO", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - DE CONOCIMIENTO ESPECIAL", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - ESPECIAL Y AUTÓNOMO", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - GARANTÍAS CONSTITUCIONALES", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - PIEZA SEPARADA", ["civil"]],
  ["CIVIL - SORTEO PRECAUT. - PROCESOS URGENTES", ["civil"]],
  ["CPC ANTERIOR - CIVIL - SORTEO COMUN - ESPECIAL", ["civil"], null, "historical"],
  ["CPC ANTERIOR - CIVIL - SORTEO COMUN - ORDINARIO", ["civil"], null, "historical"],
  ["CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ESPECIAL", ["civil"], null, "historical"],
  ["CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ESPECIALISIMO", ["civil"], null, "historical"],
  ["CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ORDINARIO", ["civil"], null, "historical"],
  ["CÁMARA CIVIL - SORTEO COMUN - DE CONOCIMIENTO", ["civil"], "appeal"],
  ["CÁMARA CIVIL - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL", ["civil"], "appeal"],
  ["CÁMARA CIVIL - SORTEO COMUN - ESPECIAL Y AUTÓNOMO", ["civil"], "appeal"],
  ["CPC ANTERIOR - PAZ - SORTEO COMUN - ESPECIAL", ["paz"], null, "historical"],
  ["PAZ - SORTEO COMUN - DE CONOCIMIENTO", ["paz"]],
  ["PAZ - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL", ["paz"]],
  ["PAZ - SORTEO COMUN - MONITORIO ESPECIAL", ["paz"]],
  ["PAZ - SORTEO PRECAUT. - DE CONOCIMIENTO", ["paz"]],
  ["ESPECIALES", ["familia"]],
  ["OTROS PROCESOS", ["familia"]],
  ["VIOLENCIA Y PROT.DERECHOS", ["familia"]],
  ["ALTA", ["laboral"]],
  ["LABORAL", ["laboral", "supreme"]],
  ["PRECAUTORIA ALTA", ["laboral"]],
  ["PRECAUTORIA BAJA", ["laboral"]],
  ["PRECAUTORIA MEDIA", ["laboral"]],
  ["COMP. ORIGINARIA", ["supreme"], "supreme"],
];

export const MENDOZA_WORKFLOW_CATEGORIES = Object.freeze(
  CATEGORY_DEFINITIONS.map(([value, fueros, instance = null, status = "active"], index) => (
    catalogEntry({
      id: `category_${String(index + 1).padStart(2, "0")}`,
      value,
      fueros,
      instance,
      status,
    })
  )),
);

const MATTERS_BY_CATEGORY = Object.freeze({
  "CAMARA CIVIL-SORTEO COMUN-ESPECIAL": ["SUCESIÓN"],
  "CAMARA CIVIL-SORTEO COMUN-ORDINARIO": [
    "D. Y P. (ACCIDENTE DE TRANSITO)",
    "DAÑOS Y PERJUICIOS",
  ],
  CIVIL: ["RECURSO EXTRAORDINARIO PROVINCIAL"],
  "CIVIL - SORTEO COMUN - DE CONOCIMIENTO": [
    "ACCIÓN DE NULIDAD",
    "ACCIÓN REAL",
    "ACCIÓN SOCIETARIA",
    "COBRO DE PESOS",
    "CUMPLIMIENTO DE CONTRATO",
    "DAÑOS DERIVADOS DE ACCIDENTES DE TRÁNSITO",
    "DAÑOS DERIVADOS DE CONTRATOS CIVILES Y COMERCIALES",
    "DAÑOS Y PERJUICIOS",
    "DESALOJO",
    "ESCRITURACIÓN",
    "EXPROPIACIÓN",
    "INCUMPLIMIENTO DE CONTRATO",
    "PROCESO DE CONOCIMIENTO",
    "REPETICIÓN DE PAGO",
  ],
  "CIVIL - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL": [
    "DIVISIÓN DE BIENES COMUNES",
    "PRESCRIPCIÓN ADQUISITIVA",
    "PROCESO DE CONOCIMIENTO ESPECIAL",
    "PROCESO DE CONSUMO",
  ],
  "CIVIL - SORTEO COMUN - ESPECIAL Y AUTÓNOMO": [
    "CANCELACIÓN DE LIBROS SOCIETARIOS",
    "CONSIGNACIÓN",
    "EJECUCIÓN DE CONVENIO O.C.C.y C.",
    "ESTIMACIÓN DE HONORARIOS",
    "HOMOLOGACIÓN DE CONVENIO O.C.C. y C.",
    "INCIDENTE",
    "INCIDENTE (art. 92 CPCCyT)",
    "PROCESO ESPECIAL Y AUTÓNOMO",
    "REGULACIÓN DE HONORARIOS",
    "SUCESIÓN",
    "SUCESIÓN TESTAMENTARIA",
    "TERCERÍA",
  ],
  "CIVIL - SORTEO COMUN - GARANTÍAS CONSTITUCIONALES": ["ACCIÓN DE AMPARO"],
  "CIVIL - SORTEO COMUN - PIEZA SEPARADA": [
    "BENEFICIO DE LITIGAR SIN GASTOS",
    "EJECUCIÓN DE HONORARIOS",
  ],
  "CIVIL - SORTEO PRECAUT. - DE CONOCIMIENTO": [
    "ACCIÓN REAL",
    "ACCIÓN SOCIETARIA",
    "DAÑOS DERIVADOS DE ACCIDENTES DE TRÁNSITO",
    "DAÑOS DERIVADOS DE VIOLENCIA DE GÉNERO",
    "DAÑOS Y PERJUICIOS",
  ],
  "CIVIL - SORTEO PRECAUT. - DE CONOCIMIENTO ESPECIAL": [
    "ACCIONES POSESORIAS Y DE TENENCIA",
    "PRESCRIPCIÓN ADQUISITIVA",
    "PROCESO DE CONSUMO",
  ],
  "CIVIL - SORTEO PRECAUT. - ESPECIAL Y AUTÓNOMO": ["SUCESIÓN"],
  "CIVIL - SORTEO PRECAUT. - GARANTÍAS CONSTITUCIONALES": ["ACCIÓN DE AMPARO"],
  "CIVIL - SORTEO PRECAUT. - PIEZA SEPARADA": ["EJECUCIÓN DE HONORARIOS"],
  "CIVIL - SORTEO PRECAUT. - PROCESOS URGENTES": [
    "ACCIÓN PREVENTIVA",
    "DILIGENCIA PRELIMINAR",
    "MEDIDA CAUTELAR",
  ],
  "CPC ANTERIOR - CIVIL - SORTEO COMUN - ESPECIAL": [
    "CPC ANTERIOR - BENEFICIO DE LITIGAR SIN GASTOS",
    "CPC ANTERIOR - EJECUCIÓN CAMBIARIA",
    "CPC ANTERIOR - EJECUCIÓN HIPOTECARIA",
    "CPC ANTERIOR - EJECUCIÓN PRENDARIA",
    "CPC ANTERIOR - SUCESIÓN",
  ],
  "CPC ANTERIOR - CIVIL - SORTEO COMUN - ORDINARIO": [
    "CPC ANTERIOR - D. Y P. (ACCIDENTE DE TRANSITO)",
    "CPC ANTERIOR - DAÑOS Y PERJUICIOS",
    "CPC ANTERIOR - DETERMINACIÓN DE CAPACIDAD",
    "CPC ANTERIOR - DIVISIÓN DE CONDOMINIO",
    "CPC ANTERIOR - PRESCRIPCIÓN ADQUISITIVA",
    "CPC ANTERIOR - RESCISIÓN DE CONTRATO",
  ],
  "CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ESPECIAL": [
    "CPC ANTERIOR - EJECUCIÓN CAMBIARIA",
    "CPC ANTERIOR - EJECUCIÓN HIPOTECARIA",
  ],
  "CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ESPECIALISIMO": [
    "CPC ANTERIOR - HOMOLOGACIÓN DE CONVENIO",
  ],
  "CPC ANTERIOR - CIVIL - SORTEO PRECAUT. - ORDINARIO": [
    "CPC ANTERIOR - DAÑOS Y PERJUICIOS",
    "CPC ANTERIOR - DIVORCIO CONSENSUAL",
    "CPC ANTERIOR - ORDINARIO",
  ],
  "CÁMARA CIVIL - SORTEO COMUN - DE CONOCIMIENTO": [
    "DAÑOS DERIVADOS DE ACCIDENTES DE TRÁNSITO",
    "SIMULACIÓN",
  ],
  "CÁMARA CIVIL - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL": [
    "PROCESO DE CONSUMO",
  ],
  "CÁMARA CIVIL - SORTEO COMUN - ESPECIAL Y AUTÓNOMO": [
    "REGULACIÓN DE HONORARIOS",
  ],
  "CPC ANTERIOR - PAZ - SORTEO COMUN - ESPECIAL": [
    "CPC ANTERIOR - EJECUCIÓN TÍPICA (COBRO DE ALQ.)",
  ],
  "PAZ - SORTEO COMUN - DE CONOCIMIENTO": ["DAÑOS Y PERJUICIOS"],
  "PAZ - SORTEO COMUN - DE CONOCIMIENTO ESPECIAL": ["PROCESOS DE CONSUMO"],
  "PAZ - SORTEO COMUN - MONITORIO ESPECIAL": ["PROCESO MONITORIO ESPECIAL"],
  "PAZ - SORTEO PRECAUT. - DE CONOCIMIENTO": ["DAÑOS DERIVADOS DE LOCACIÓN"],
  ESPECIALES: [
    "ALIMENTOS DEFINITIVOS",
    "DIVORCIO BILATERAL",
    "EJECUCIÓN DE ALIMENTOS",
  ],
  "OTROS PROCESOS": ["HOMOLOGACIÓN DE CONVENIO"],
  "VIOLENCIA Y PROT.DERECHOS": ["MEDIDAS DE PROTECCIÓN DE DERECHOS"],
  ALTA: ["ACCIDENTE", "DESPIDO", "ENFERMEDAD ACCIDENTE"],
  "PRECAUTORIA ALTA": ["ACCIDENTE", "DESPIDO", "ENFERMEDAD ACCIDENTE"],
  "PRECAUTORIA BAJA": ["EJECUCIÓN DE CONVENIO Y HONORARIOS"],
  "PRECAUTORIA MEDIA": ["HOMOLOGACIONES"],
  LABORAL: ["RECURSO EXTRAORDINARIO PROVINCIAL"],
  "COMP. ORIGINARIA": [
    "ACCIÓN PROCESAL ADMINISTRATIVA (ART. 2 INC. 4 LEY 9.423; ART. 1° CPA; ART. 187 LEY 9003)",
  ],
});

const MATTER_ALIASES = Object.freeze({
  "DIVORCIO BILATERAL": ["Divorcio bilateral"],
  "HOMOLOGACIÓN DE CONVENIO": ["Homologación de convenio"],
  "MEDIDAS DE PROTECCIÓN DE DERECHOS": [" Medidas de protección de derechos"],
  "EJECUCIÓN DE CONVENIO Y HONORARIOS": ["EJECUCION DE CONVENIO Y HONORARIOS"],
  HOMOLOGACIONES: ["HOMOLOGACIONES "],
  "CPC ANTERIOR - RESCISIÓN DE CONTRATO": ["CPC ANTERIOR - rescisión DE CONTRATO"],
  "CPC ANTERIOR - HOMOLOGACIÓN DE CONVENIO": ["CPC ANTERIOR - HomologaciónDECONVENIO"],
  "CPC ANTERIOR - DIVORCIO CONSENSUAL": ["CPC ANTERIOR - DIVORCIOCONSENSUAL"],
});

const categoryByValue = new Map(
  MENDOZA_WORKFLOW_CATEGORIES.map((entry) => [entry.value, entry]),
);
const matterDefinitions = new Map();
Object.entries(MATTERS_BY_CATEGORY).forEach(([category, matters]) => {
  matters.forEach((value) => {
    const key = normalizeCatalogText(value);
    const current = matterDefinitions.get(key) || {
      value,
      categories: new Set(),
      fueros: new Set(),
    };
    current.categories.add(category);
    categoryByValue.get(category)?.fueros.forEach((fuero) => (
      current.fueros.add(fuero)
    ));
    matterDefinitions.set(key, current);
  });
});

export const MENDOZA_WORKFLOW_MATTERS = Object.freeze(
  [...matterDefinitions.values()].map((definition, index) => catalogEntry({
    id: `matter_${String(index + 1).padStart(3, "0")}`,
    value: definition.value,
    aliases: MATTER_ALIASES[definition.value] || [],
    categories: [...definition.categories],
    fueros: [...definition.fueros],
    status: definition.value.startsWith("CPC ANTERIOR")
      ? "historical"
      : "active",
  })),
);

const ORGANISM_DEFINITIONS = [
  ["018001", "SUPREMA CORTE DE JUSTICIA - SALA PRIMERA", ["supreme"], null, "supreme"],
  ["018003", "SUPREMA CORTE DE JUSTICIA - SALA SEGUNDA", ["supreme"], null, "supreme"],
  ["018013", "SUPREMA CORTE DE JUSTICIA - SECRETARIA DE COMPETENCIA ORIGINARIA", ["supreme"], null, "supreme"],
  ["010301", "CAMARAS DE APELACIONES EN LO CIVIL, C-PRIMERA", ["civil"], 1, "appeal"],
  ["010302", "CAMARAS DE APELACIONES EN LO CIVIL, C-SEGUNDA", ["civil"], 1, "appeal"],
  ["010303", "CAMARAS DE APELACIONES EN LO CIVIL, C-TERCERA", ["civil"], 1, "appeal"],
  ["010304", "CAMARAS DE APELACIONES EN LO CIVIL, C-CUARTA", ["civil"], 1, "appeal"],
  ["010305", "QUINTA CAMARA DE APELACIONES EN LO CIVIL - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["civil"], 1, "appeal"],
  ["012144", "MESA DE ENTRADA CENTRAL CIVIL - PRIMERA CIRCUNSCRIPCIÓN DE MENDOZA", ["civil"], 1, "intake"],
  ["012051", "TRIBUNAL DE GESTION ASOCIADA-PRIMERO", ["civil"], 1, "first"],
  ["012052", "TRIBUNAL DE GESTION ASOCIADA-SEGUNDO", ["civil"], 1, "first"],
  ["012053", "TRIBUNAL DE GESTION ASOCIADA-TERCERO", ["civil"], 1, "first"],
  ["012054", "TRIBUNAL DE GESTION ASOCIADA-CUARTO", ["civil"], 1, "first"],
  ["012003", "JUZGADOS EN LO CIVIL, COMERCIAL Y MIN-TERCERO", ["civil"], 1, "first"],
  ["012012", "JUZGADOS EN LO CIVIL, COMERCIAL Y MIN-DECIMO SEGUNDO", ["civil"], 1, "first"],
  ["020301", "CAMARA DE APELACIONES EN LO CIVIL, CO-PRIMERA - 2DA CIRC.", ["civil"], 2, "appeal"],
  ["020302", "CAMARA DE APELACIONES EN LO CIVIL, CO-SEGUNDA - 2DA CIRC.", ["civil"], 2, "appeal"],
  ["022051", "TRIBUNAL GESTION JUDICIAL ASOCIADA N° 1- 2DA CIRC.", ["civil"], 2, "first"],
  ["022003", "JUZGADO EN LO CIVIL, COMERCIAL Y MINA-TERCERO - GENERAL ALVEAR", ["civil"], 2, "first"],
  ["022006", "JUZGADO EN LO CIVIL, COMERCIAL Y MINA-SEXTO - MALARGÜE", ["civil"], 2, "first"],
  ["032051", "JUZGADOS EN LO CIVIL, COMERCIAL Y MIN- TRIBUNAL DE GESTION ASOCIADA - 3RA. CIRC.", ["civil", "paz"], 3, "first"],
  ["032003", "JUZGADOS EN LO CIVIL, COMERCIAL Y MIN-RIVADAVIA", ["civil"], 3, "first"],
  ["042051", "TRIBUNAL DE GESTION ASOCIADA CIVIL- TUNUYAN - 4TA CIRC.", ["civil"], 4, "first"],
  ["010401", "PRIMERA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010402", "SEGUNDA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010403", "TERCERA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010404", "CUARTA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010405", "QUINTA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010406", "SEXTA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["010407", "SEPTIMA CAMARA DEL TRABAJO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["laboral"], 1, "first"],
  ["020401", "CAMARAS DEL TRABAJO-PRIMERA - 2DA CIRC.", ["laboral"], 2, "first"],
  ["020402", "CAMARAS DEL TRABAJO-SEGUNDA - 2DA CIRC.", ["laboral"], 2, "first", "historical"],
  ["033001", "CAMARAS DEL TRABAJO, DE PAZ Y TRIBUTA-PRIMERA - 3RA CIRC.", ["laboral", "paz", "tributario"], 3, "first"],
  ["033002", "CAMARAS DEL TRABAJO, DE PAZ Y TRIBUTA-SEGUNDA - 3RA CIRC.", ["laboral", "paz", "tributario"], 3, "first", "historical"],
  ["040401", "PRIMERA CAMARA DEL TRABAJO, DE PAZ Y TRIBUTARIO - 4TA CIRC.", ["laboral", "paz", "tributario"], 4, "first"],
  ["017251", "GEJUAF CAPITAL", ["familia"], 1, "first"],
  ["017255", "GEJUAF GUAYMALLEN", ["familia"], 1, "first"],
  ["017253", "GEJUAF GODOY CRUZ", ["familia"], 1, "first"],
  ["017254", "GEJUAF LAS HERAS", ["familia"], 1, "first"],
  ["family_lujan", "GESTIÓN JUDICIAL ASOCIADA DE FAMILIA DE LUJÁN DE CUYO", ["familia"], 1, "first"],
  ["family_maipu", "GESTIÓN JUDICIAL ASOCIADA DE FAMILIA DE MAIPÚ", ["familia"], 1, "first"],
  ["family_lavalle", "JUZGADO DE FAMILIA DE LAVALLE", ["familia"], 1, "first"],
  ["family_appeal", "CÁMARA DE APELACIONES DE FAMILIA", ["familia"], 1, "appeal"],
  ["family_malargue", "JUZGADO DE FAMILIA DE MALARGÜE", ["familia"], 2, "first"],
  ["family_alvear", "JUZGADO DE FAMILIA DE GENERAL ALVEAR", ["familia"], 2, "first"],
  ["family_san_rafael", "PRIMERA GESTIÓN JUDICIAL ASOCIADA DE FAMILIA DE SAN RAFAEL", ["familia"], 2, "first"],
  ["family_rivadavia", "TERCER JUZGADO DE FAMILIA DE RIVADAVIA", ["familia"], 3, "first"],
  ["family_san_martin", "TRIBUNAL DE GESTIÓN JUDICIAL DE FAMILIA DE SAN MARTÍN", ["familia"], 3, "first"],
  ["family_san_carlos", "JUZGADO DE FAMILIA DE SAN CARLOS", ["familia"], 4, "first"],
  ["045051", "GEJUAF TUNUYAN", ["familia"], 4, "first"],
  ["family_tupungato", "JUZGADO DE FAMILIA DE TUPUNGATO", ["familia"], 4, "first"],
  ["011851", "TRIBUNAL DE GESTION ASOCIADA DE PAZ-PRIMERO", ["paz"], 1, "first"],
  ["011811", "JUZGADOS DE PAZ LETRADOS-SEXTO", ["paz"], 1, "first"],
  ["011815", "JUZGADOS DE PAZ LETRADOS-OCTAVO", ["paz"], 1, "first"],
  ["011601", "JUZGADO DE PAZ LETRADO DE LAS HERAS", ["paz"], 1, "first"],
  ["011605", "JUZGADO DE PAZ LETRADO Y CONTRAVENCIONAL DE RODEO DE LA CRUZ", ["paz", "contravencional"], 1, "first"],
  ["011608", "JUZGADO DE PAZ LETRADO Y CONTRAVENCIONAL DE USPALLATA", ["paz", "contravencional"], 1, "first"],
  ["011702", "JUZGADOS DE PAZ LETRADOS Y CONTRAVENCIONALES-MAIPU", ["paz", "contravencional"], 1, "first"],
  ["011751", "TRIBUNAL DE GESTIÓN ASOCIADA DE LUJÁN DE CUYO CON COMPETENCIA DE PAZ Y CONTRAVENCIONAL", ["paz", "contravencional"], 1, "first"],
  ["022651", "TRIBUNAL DE GESTION ASOCIADA DE PAZ - SAN RAFAEL- 2DA CIRC.", ["paz"], 2, "first"],
  ["032661", "TRIBUNAL DE GESTION ASOCIADA DE PAZ - 3RA CIRC.", ["paz"], 3, "first"],
  ["peace_tunuyan", "JUZGADO DE PAZ LETRADO, CONTRAVENCIONAL Y TRIBUTARIO DE TUNUYÁN", ["paz", "contravencional", "tributario"], 4, "first"],
  ["peace_tupungato", "JUZGADO DE PAZ LETRADO, CONTRAVENCIONAL Y TRIBUTARIO DE TUPUNGATO", ["paz", "contravencional", "tributario"], 4, "first"],
  ["peace_la_consulta", "JUZGADO DE PAZ LETRADO Y CONTRAVENCIONAL DE LA CONSULTA", ["paz", "contravencional", "tributario"], 4, "first"],
  ["011901", "PRIMER JUZGADO DE PROCESOS CONCURSALES PRIMERA CIRCUNSCRIPCION JUDICIAL", ["concursal"], 1, "first"],
  ["011902", "SEGUNDO JUZGADO DE PROCESOS CONCURSALES - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["concursal"], 1, "first"],
  ["011903", "TERCER JUZGADO DE PROCESOS CONCURSALES PRIMERA CIRCUNSCRIPCION JUDICIAL", ["concursal"], 1, "first"],
  ["concursal_2", "PRIMER JUZGADO DE PROCESOS CONCURSALES - 2DA CIRC.", ["concursal"], 2, "first"],
  ["concursal_3", "JUZGADO DE CONCURSOS Y QUIEBRAS - 3RA CIRC.", ["concursal"], 3, "first"],
  ["012210", "PRIMER JUZGADO TRIBUTARIO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["tributario"], 1, "first"],
  ["012220", "SEGUNDO JUZGADO TRIBUTARIO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["tributario"], 1, "first"],
  ["012230", "TERCER JUZGADO TRIBUTARIO - PRIMERA CIRCUNSCRIPCION DE MENDOZA", ["tributario"], 1, "first"],
];

export const MENDOZA_WORKFLOW_ORGANISMS = Object.freeze(
  ORGANISM_DEFINITIONS.map(([
    id,
    value,
    fueros,
    circunscripcion,
    instance,
    status = "active",
  ]) => catalogEntry({
    id: `organism_${id}`,
    value,
    label: value,
    fueros,
    circunscripcion,
    instance,
    status,
  })),
);

export const MENDOZA_WORKFLOW_COURT_LEVELS = Object.freeze([
  catalogEntry({
    id: "court_level_tribunals",
    value: "TRIBUNALES",
    label: "Tribunales",
    instance: "first",
  }),
  catalogEntry({
    id: "court_level_appeal",
    value: "CÁMARA",
    label: "Cámara",
    instance: "appeal",
  }),
  catalogEntry({
    id: "court_level_supreme",
    value: "SUPREMA CORTE",
    label: "Suprema Corte",
    instance: "supreme",
  }),
]);

function entryMatches(entry, value) {
  const normalized = normalizeCatalogText(value);
  return normalized && [entry.value, entry.label, ...(entry.aliases || [])]
    .some((candidate) => normalizeCatalogText(candidate) === normalized);
}

export function mendozaWorkflowCourtLevel(value) {
  const normalized = normalizeCatalogText(value);
  if (!normalized) return "";
  if (normalized.includes("SUPREMA") || normalized.includes("CORTE")) {
    return "SUPREMA CORTE";
  }
  if (
    normalized.includes("CAMARA")
    && !normalized.includes("CAMARA DEL TRABAJO")
  ) {
    return "CÁMARA";
  }
  return "TRIBUNALES";
}

function fueroKey(value) {
  const match = MENDOZA_WORKFLOW_FUEROS.find((entry) => (
    entryMatches(entry, value)
  ));
  if (!match) return null;
  return match.fueros[0] || match.id.replace(/_(?:raw|appellate_raw)$/u, "");
}

function groupLabel(entry) {
  if (entry.id.startsWith("court_level_")) return "Instancia actual";
  if (entry.circunscripcion) {
    const instance = {
      appeal: "Cámara",
      first: "Primera instancia",
      intake: "Mesa de entradas",
      supreme: "Suprema Corte",
    }[entry.instance] || "Organismos";
    return `${entry.circunscripcion}.ª circunscripción · ${instance}`;
  }
  if (entry.instance === "supreme") return "Suprema Corte";
  return entry.status === "historical" ? "Valores históricos" : "Opciones";
}

function publicOption(entry) {
  return {
    id: entry.id,
    value: entry.value,
    label: entry.label,
    aliases: [...entry.aliases],
    group: groupLabel(entry),
    status: entry.status,
    circunscripcion: entry.circunscripcion,
    instance: entry.instance,
    fueros: [...entry.fueros],
  };
}

export function isMendozaWorkflowCatalogProperty(property) {
  return MENDOZA_WORKFLOW_CATALOG_PROPERTIES.includes(property);
}

export function mendozaWorkflowCatalogOptions(property, context = {}) {
  const selectedFuero = fueroKey(context.fuero);
  const selectedCategory = MENDOZA_WORKFLOW_CATEGORIES.find((entry) => (
    entryMatches(entry, context.procedureType)
  ));
  let entries;

  if (property === "fuero") entries = MENDOZA_WORKFLOW_FUEROS;
  else if (property === "procedure_type") {
    entries = MENDOZA_WORKFLOW_CATEGORIES.filter((entry) => (
      !selectedFuero || entry.fueros.includes(selectedFuero)
    ));
  } else if (property === "materia") {
    entries = MENDOZA_WORKFLOW_MATTERS.filter((entry) => (
      selectedCategory
        ? entry.categories.includes(selectedCategory.value)
        : !selectedFuero || entry.fueros.includes(selectedFuero)
    ));
  } else if (property === "organismo") {
    entries = MENDOZA_WORKFLOW_COURT_LEVELS;
  } else return [];

  return entries.map(publicOption).sort((left, right) => (
    left.group.localeCompare(right.group, "es-AR")
    || left.label.localeCompare(right.label, "es-AR")
  ));
}

export function findMendozaWorkflowCatalogOption(
  property,
  value,
  context = {},
) {
  if (!String(value || "").trim()) return null;
  const visible = mendozaWorkflowCatalogOptions(property, context);
  if (property === "organismo") {
    const courtLevel = mendozaWorkflowCourtLevel(value);
    return visible.find((entry) => entry.value === courtLevel) || null;
  }
  const match = visible.find((entry) => entryMatches(entry, value));
  if (match) return match;
  const catalogs = {
    fuero: MENDOZA_WORKFLOW_FUEROS,
    procedure_type: MENDOZA_WORKFLOW_CATEGORIES,
    materia: MENDOZA_WORKFLOW_MATTERS,
    organismo: MENDOZA_WORKFLOW_COURT_LEVELS,
  };
  const fallback = catalogs[property]?.find((entry) => entryMatches(entry, value));
  return fallback ? publicOption(fallback) : null;
}

